import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { macShortcuts, windowsShortcuts } from '../KeyboardShortcuts/ShortcutsKeymap'
import MacShift from '../../assets/icons/MacShift'
import MacCommand from '../../assets/icons/MacCommand'
import MacOption from '../../assets/icons/MacOption'
import ArrowRightKeyboard from '../../assets/icons/ArrowRightKeyboard'
import ArrowLeftKeyboard from '../../assets/icons/ArrowLeftKeyboard'
import ArrowDownKeyboard from '../../assets/icons/ArrowDownKeyboard'
import ArrowUpKeyboard from '../../assets/icons/ArrowUpKeyboard'
import { isMacOS } from '../../util/general'

type ShortcutKeyMap = Record<string, string>;
type ShortcutsCategories = Record<string, ShortcutKeyMap>;

interface ShortcutRendererProps {
    shortcutName: string;
}

interface KeyBoxProps {
    keyLabel: string;
    isMacOS: boolean;
}

interface KeyBoxProps {
    keyLabel: string;
    isMacOS: boolean;
}

const KeyBox: React.FC<KeyBoxProps> = ({ keyLabel, isMacOS }) => {
    const renderContent = () => {
        if (isMacOS) {
            switch (keyLabel) {
                case 'Shift':
                    return <MacShift />
                case 'Meta':
                    return <MacCommand />
                case 'Alt':
                    return <MacOption />
                case 'ArrowUp':
                    return <ArrowUpKeyboard />
                case 'ArrowDown':
                    return <ArrowDownKeyboard />
                case 'ArrowLeft':
                    return <ArrowLeftKeyboard />
                case 'ArrowRight':
                    return <ArrowRightKeyboard />
                default:
                    return <Typography color={'textSecondary'}>{keyLabel}</Typography>
            }
        } else {
            return <Typography color={'textSecondary'}>{keyLabel}</Typography>
        }
    }

    return (
        <Box
            sx={{
                minWidth: '32px',
                minHeight: '32px',
                borderRadius: '6px',
                border: (theme) => `0.5px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (theme) => `0 2px 0 ${theme.palette.divider}`
            }}
        >
            {renderContent()}
        </Box>
    )
}

const ShortcutRenderer: React.FC<ShortcutRendererProps> = ({ shortcutName }) => {
    const shortcuts: ShortcutsCategories = isMacOS() ? macShortcuts : windowsShortcuts

    const findShortcut = (): string | null => {
        for (const category in shortcuts) {
            if (shortcuts[category][shortcutName]) {
                return shortcuts[category][shortcutName]
            }
        }
        return null
    }

    const shortcut = findShortcut()

    if (shortcut) {
        const keys = shortcut.split('+')
        return (
            <Stack direction="row" spacing={1}>
                {keys.map((key, index) => (
                    <Stack spacing={1} direction={'row'} alignItems={'center'}>
                        <KeyBox keyLabel={key} isMacOS={isMacOS()} />
                        {index < keys.length - 1 && <Typography color={'textSecondary'} variant="body1">+</Typography>}
                    </Stack>
                ))}
            </Stack>
        )
    }
    return null
}

export default ShortcutRenderer
